import { useState } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Button from '@mui/material/Button'
import LinearProgress from '@mui/material/LinearProgress'
import Alert from '@mui/material/Alert'
import Typography from '@mui/material/Typography'
import { useCourtsQuery } from '../hooks/useCourtsQuery'
import { useSeedTokenQuery } from '../hooks/useSeedTokenQuery'
import { useCurrentUser } from '../../../shared/hooks/useCurrentUser'
import { ROBOTS_CONSTANTS } from '../robotsConstants'

function formatCreatedAt(isoDate) {
  if (!isoDate) return ''
  const parsed = new Date(isoDate)
  if (Number.isNaN(parsed.getTime())) return isoDate

  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(parsed)
}

function SeedTokenTab() {
  const [login, setLogin] = useState('')
  const [selectedCourt, setSelectedCourt] = useState(null)
  const [isPje, setIsPje] = useState(false)

  const { allowedToUseRobots, isLoading: isLoadingUser } = useCurrentUser()
  const { data: courts = [], isLoading: isLoadingCourts } = useCourtsQuery()
  const { result, isLoading, isError, error, search, reset } = useSeedTokenQuery()

  function handleLoginChange(event) {
    reset()
    setLogin(event.target.value)
  }

  function handleCourtChange(_, newValue) {
    reset()
    setSelectedCourt(newValue)
  }

  function handlePjeChange(event) {
    reset()
    setIsPje(event.target.checked)
  }

  function handleSearch() {
    const court = selectedCourt?.site_name
    const tribunal = isPje ? `${court}${ROBOTS_CONSTANTS.PJE_SUFFIX}` : court
    search({ login, tribunal })
  }

  const canSearch = Boolean(login) && Boolean(selectedCourt) && !isLoading && allowedToUseRobots
  const errorMessage = error?.response?.data?.errorMessage || 'Erro na requisição'

  return (
    <Box>
      <Paper sx={{ p: 3 }} variant="outlined">
        <Stack spacing={2}>
          {!isLoadingUser && !allowedToUseRobots && (
            <Alert severity="warning">Você não tem permissão para usar esta ferramenta.</Alert>
          )}

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Login"
              value={login}
              onChange={handleLoginChange}
              fullWidth
            />
            <Autocomplete
              options={courts}
              getOptionLabel={(option) => option.description || ''}
              isOptionEqualToValue={(option, value) => option.site_name === value.site_name}
              value={selectedCourt}
              onChange={handleCourtChange}
              loading={isLoadingCourts}
              disabled={isLoadingCourts}
              noOptionsText="Nenhum tribunal encontrado"
              loadingText="Carregando tribunais..."
              slotProps={{ listbox: { style: { maxHeight: 320 } } }}
              renderInput={(params) => <TextField {...params} label="Tribunal" />}
              fullWidth
            />
          </Stack>

          <FormControlLabel
            control={<Checkbox checked={isPje} onChange={handlePjeChange} />}
            label="PJe"
          />

          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={handleSearch} disabled={!canSearch}>
              Buscar
            </Button>
          </Stack>

          {isLoading && <LinearProgress />}

          {isError && <Alert severity="error">{errorMessage}</Alert>}

          {result && (
            <Alert severity="success">
              <Typography component="div">
                Token: <strong>{result.token}</strong>
              </Typography>
              {result.createdAt && (
                <Typography component="div" variant="body2">
                  Criação: {formatCreatedAt(result.createdAt)}
                </Typography>
              )}
            </Alert>
          )}
        </Stack>
      </Paper>
    </Box>
  )
}

export default SeedTokenTab
